import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { colorConstants } from 'config/colorConstants';
import { Spin, Switch, Tabs } from 'antd';
import { TPODataContext } from 'contexts/TPOContext';

import { CustomModal } from '../../PricingSimulator/Modals/CreateEditProjectContainer';
import dayjs, { Dayjs } from 'dayjs';

//updating antd config from moment to dayjs
//antd uses moment js by default for reading value
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { antIcon } from 'components/pages/Login';
import EventDetails from '../AddEventModal/EventDetails';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { formatDatePreservingTimeValues, getConfig, sendNotification } from 'utils/helperMethods';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';

import SearchBox from 'components/molecules/SearchBox';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { getImportedEventsTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import { CustomTable } from 'components/molecules/PricingSimulator/ScenarioTableWrapper';
import { useFetchEventsForEventLibrary } from 'hooks/tradePromoOptimization/events/useFetchEventsForEventLibrary';
import { queryClient } from 'services/reactQueryService';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { UserContext } from 'contexts/ConfigContext';
import { useFetchEventsForImportEventLibrary } from 'hooks/tradePromoOptimization/events/useFetchEventsForImportEventLibrary';
//updated component that uses dayjs instead of moment
const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

type Props = {
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish: Function;
};

const ImportEventFromEventLibraryModal: FC<Props> = ({ open, onCancel, onSubmitFinish }) => {
    const { dimensionConfigData, selectedPlanInfo, isTpmTab } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const limit = tpoConfig?.tableLimit;

    //default variable
    const defaultEventsVariableObject = {
        search_string: '',
        target_plan_id: selectedPlanInfo?.planId,
        offset: 0,
        limit: limit,
    };

    const [searchKey, setSearchKey] = useState<string>('');
    const [checkboxSelected, setCheckboxSelected] = useState<any>({});
    const [eventsDataVariable, setEventsDataVariable] = useState({ ...defaultEventsVariableObject });
    const modalTitle = `Import from Event Library`;
    const modalButtonTitle = `Import`;

    function resetFields() {
        setCheckboxSelected({});
        setSearchKey('');
        setEventsDataVariable({ ...defaultEventsVariableObject });
    }

    const { mutate: importEvent, loading: importEventLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.importEventsFromPlanUrl,
        method: 'post',
    });

    //events data api
    const {
        data: eventsData,
        loading: eventsDataLoading,
        error: eventsDataError,
        count: eventsDataCount,
    } = useFetchEventsForImportEventLibrary({
        method: 'post',
        queryConfig: { enabled: open && Boolean(eventsDataVariable.target_plan_id) },
        url: tradePromoOptimizationUrls.getEventsForEventsLibraryUrl,
        queryKey: ['tpo-event-library-events-data', eventsDataVariable],
        variables: { ...(eventsDataVariable || {}) },
    });

    useEffect(() => {
        //cleanup function
        return () => {
            resetFields();
            queryClient.removeQueries({
                queryKey: ['tpo-event-library-events-data', eventsDataVariable],
                exact: true,
            });
        };
    }, [open, selectedPlanInfo]);

    const onSubmit = () => {
        if (importEventLoading || importButtonDisabledCondition) return;
        //validate form and trigger api call
        const allEventSelected = Boolean(checkboxSelected?.['selectAll']);
        const tempCheckboxSelected = { ...checkboxSelected };
        delete tempCheckboxSelected?.['selectAll'];

        const eventPromoPair = Object.keys(tempCheckboxSelected).map((item) => {
            return {
                event_id: item,
                promo_ids: tempCheckboxSelected[item],
            };
        });
        // const importEventFromEventLibrary = {
        //     event_promo_pairs: [
        //         { event_id: 'vs123', promo_ids: ['asd1', 'jk123'] },
        //         { event_id: 'as323', promo_ids: ['juwu31', 2, 3] },
        //     ],
        // };

        const payload = {
            all_events_selected: allEventSelected,
            event_promo_pairs: eventPromoPair,
            target_plan_id: selectedPlanInfo?.planId,
            user_id: localStorage.getItem('email'),
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
            is_tpm_tab: isTpmTab,
        };
        // form.submit();
        importEvent(payload, {
            onSuccess: (data: any) => {
                sendNotification('success', tpoNotificationMessages.IMPORT_EVENT_SUCCESS);
                onSubmitFinish();
            },
            onError: (val: any) => {
                sendNotification('error', ErrorMessages.GENERALIZED_ERROR_MSG);
                onCancel(val);
            },
        });
    };

    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        setEventsDataVariable({ ...eventsDataVariable, offset: 0, search_string: val });
    };

    //events list inf scroller function
    function infiniteScrollOnNextEvents(loading: boolean) {
        if (loading) return;
        const newOffset = eventsDataVariable?.offset + eventsDataVariable?.limit;
        setEventsDataVariable({ ...eventsDataVariable, offset: newOffset });
    }

    const memoisedColumns = useMemo(() => {
        const columns = [
            { label: 'Event', key: 'event', sortKey: 'event' },
            { label: 'Promo', key: 'promo', sortKey: 'promo' },
        ];
        return getImportedEventsTableColumns(
            columns,
            eventsData,
            checkboxSelected,
            setCheckboxSelected,
            eventsDataCount,
        );
    }, [eventsData, checkboxSelected]);

    const importButtonDisabledCondition = Object.keys(checkboxSelected).length === 0;

    return (
        <div>
            <CustomModal
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ disabled: importEventLoading, style: { borderRadius: 6 } }}
                okButtonProps={{ disabled: importButtonDisabledCondition, style: { borderRadius: 6 } }}
                okText={importEventLoading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : modalButtonTitle}
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                width={720}
                onCancel={(e) => {
                    if (importEventLoading) return;
                    onCancel(e);
                }}
                title={<div className="flex items-center">{modalTitle} </div>}
            >
                <div className="mb-2">
                    <SearchBox onSearch={onSearch} search={searchKey} setSearch={setSearchKey} />
                </div>
                <InfiniteScroller
                    data={eventsData}
                    loading={eventsDataLoading}
                    hasMore={eventsData?.length < eventsDataCount || eventsDataLoading}
                    height={eventsData && !eventsDataError ? 500 ?? undefined : 'auto'}
                    //height={200}
                    //     loader={true}
                    onNext={() => infiniteScrollOnNextEvents(eventsDataLoading)}
                >
                    <CustomTable
                        onChange={(pagination, filter, sort: any, extra) => {
                            if (sort?.order) {
                                const sortMapping: any = {
                                    descend: 'desc',
                                    ascend: 'asc',
                                };

                                // setTableReactQueryVariables({
                                //     ...tableReactQueryVariables,
                                //     sort_by: sort?.column?.sortKey,
                                //     sorting_order: sortMapping?.[sort?.order] || 'desc',
                                //     offset: 0,
                                // });
                                //default it to dim1 column on cancelling sorting
                            } else {
                                // setTableReactQueryVariables({
                                //     ...tableReactQueryVariables,
                                //     sort_by: 'updated_at',
                                //     sorting_order: 'desc',
                                //     offset: 0,
                                // });
                            }
                        }}
                        sticky={{
                            getContainer: () =>
                                document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                        }} //adding x axis scrollbar on table
                        //loading={eventsDataLoading}
                        columns={memoisedColumns}
                        scroll={{ x: '100%' }}
                        dataSource={eventsData}
                        pagination={false}
                        style={{
                            opacity: eventsDataLoading ? '0.3' : 1,
                            pointerEvents: eventsDataLoading ? 'none' : 'auto',
                        }}
                        locale={{
                            emptyText: eventsDataError ? <ErrorBox msg={String(eventsDataError)} /> : '',
                        }}
                        rowClassName={(record: any) => {
                            let className = '';

                            //if expired, then disabled row
                            if (record?.status === 'expired') {
                                className += 'opacity-60 cursor-not-allowed pointer-events-none';
                            }
                            return className;
                        }}
                    />
                </InfiniteScroller>
            </CustomModal>
        </div>
    );
};

export default ImportEventFromEventLibraryModal;
